import { AnalyticsBrowser } from '@segment/analytics-next'
import mixpanel from 'mixpanel-browser'
import { createContext } from 'react'
import {
  AnalyticsPageName,
  AnalyticsUserTraits,
} from 'src/analytics/analytics.models'
import { Userpilot } from 'userpilot'

export interface AnalyticsContextProps {
  analytics: ExtendAnalyticsBrowser
}

type Mixpanel = typeof mixpanel

export class ExtendAnalyticsBrowser {
  public loggingEnabled = false
  public mixpanelIsEnabled = true
  public isCspAuthenticated = false

  private readonly segmentInstance: AnalyticsBrowser
  private readonly _mixpanelInstance: Mixpanel
  private userTraits: AnalyticsUserTraits | undefined = undefined
  private eventOnlyProperties: Record<string, any> = {
    platform: 'web',
  }

  public constructor(
    segmentInstance?: AnalyticsBrowser,
    mixpanelInstance?: Mixpanel,
  ) {
    this.segmentInstance =
      segmentInstance !== undefined ? segmentInstance : new AnalyticsBrowser()
    this._mixpanelInstance =
      mixpanelInstance !== undefined ? mixpanelInstance : mixpanel
  }

  private get mixpanelInstance(): Mixpanel | undefined {
    return this.mixpanelIsEnabled ? this._mixpanelInstance : undefined
  }

  public track = (
    event: string,
    properties?: Record<string, unknown>,
  ): void => {
    const combinedProperties = {
      ...this.userTraits,
      ...properties,
      ...this.eventOnlyProperties,
    }
    if (this.loggingEnabled) {
      /* eslint-disable-next-line no-console */
      console.info('analytics track event', event, combinedProperties)
    }
    Userpilot?.track(event, combinedProperties)
    if (!this.isCspAuthenticated) {
      this.mixpanelInstance?.track(event, combinedProperties)
      this.segmentInstance?.track(event, combinedProperties)
    }
  }

  public page = (
    pageName: AnalyticsPageName,
    properties?: Record<string, unknown>,
  ): void => {
    this.pageInternal(pageName, properties)
  }

  /**
   * @deprecated: Use the type-safe {@link `page`} function instead
   */
  public pageLegacy = (
    pathName: string,
    properties?: Record<string, unknown>,
  ): void => {
    this.pageInternal(pathName, properties)
  }

  private pageInternal = (
    pageName: string,
    properties?: Record<string, unknown>,
  ): void => {
    const combinedProperties = {
      ...this.userTraits,
      ...properties,
      ...this.eventOnlyProperties,
    }
    if (this.loggingEnabled) {
      /* eslint-disable-next-line no-console */
      console.info('analytics page event', pageName, combinedProperties)
    }
    Userpilot?.track(pageName, combinedProperties)
    if (!this.isCspAuthenticated) {
      this.mixpanelInstance?.track(pageName, combinedProperties)
      this.segmentInstance?.page(pageName, combinedProperties)
    }
  }

  public identify = (
    userId: string,
    newUserTraits: AnalyticsUserTraits,
  ): void => {
    this.userTraits = newUserTraits
    if (this.loggingEnabled) {
      /* eslint-disable-next-line no-console */
      console.info(
        'analytics identify event with user traits',
        userId,
        newUserTraits,
      )
    }
    Userpilot?.identify(userId, newUserTraits)
    // "Calling userpilot.reload function should only be called after identifying the user."
    // https://docs.userpilot.com/article/63-calling-identify-vs-reload-methods
    Userpilot?.reload()
    if (!this.isCspAuthenticated) {
      this.mixpanelInstance?.identify(userId)
      this.mixpanelInstance?.people.set({ ...newUserTraits })
      this.mixpanelInstance?.register({
        subscription_plan_type: newUserTraits.subscription_plan_type,
      })
      this.segmentInstance?.identify(userId, { ...newUserTraits })
    }
  }

  public get lastIdentifiedUserTraits(): AnalyticsUserTraits | undefined {
    return this.userTraits
  }

  public reset = (): void => {
    if (this.loggingEnabled) {
      /* eslint-disable-next-line no-console */
      console.info('analytics reset event')
    }
    this.segmentInstance?.reset()
    this.mixpanelInstance?.reset()
    this.userTraits = undefined
  }
}

const defaultAnalyticsContext = {
  analytics: new ExtendAnalyticsBrowser(),
}

export const AnalyticsContext = createContext<AnalyticsContextProps>(
  defaultAnalyticsContext,
)

export const analyticsPageNames: Record<string, AnalyticsPageName> = {
  '/accounts/create': AnalyticsPageName.CreateAccount,
  '/accounts/youraccounts': AnalyticsPageName.YourAccounts,
  '/accounts': AnalyticsPageName.YourAccounts,
  '/accounts/:id/overview': AnalyticsPageName.AccountOverview,
  '/accounts/:id/budgets': AnalyticsPageName.AccountBudgetList,
  '/accounts/:id/virtualcards': AnalyticsPageName.AccountVirtualCards,
  '/accounts/:id/transactions': AnalyticsPageName.AccountTransactions,
  '/accounts/:id/settings': AnalyticsPageName.AccountSettings,
  '/accounts/:id/settings/close': AnalyticsPageName.CloseAccount,

  '/billpay': AnalyticsPageName.BillPay,
  '/billpay/create': AnalyticsPageName.CreateBillPayVirtualCardFlyout,
  '/billpay/:id': AnalyticsPageName.BillPayVirtualCardDetail,
  '/billpay/:id/update': AnalyticsPageName.UpdateBillPayVirtualCardFlyout,
  '/billpay/:id/deactivate': AnalyticsPageName.DeactivateBillPayVirtualCard,
  '/billpay/:id/close': AnalyticsPageName.CloseBillPayVirtualCard,

  '/budgets/all': AnalyticsPageName.AllBudgets,
  '/budgets/yours': AnalyticsPageName.YourBudgets,
  '/budgets/pending': AnalyticsPageName.PendingBudgets,
  '/budgets/needsapproval': AnalyticsPageName.BudgetsNeedsApproval,
  '/budgets/create': AnalyticsPageName.CreateBudgetFlyout,
  '/budgets/request': AnalyticsPageName.RequestBudgetFlyout,
  '/budgets/:id/overview': AnalyticsPageName.BudgetOverview,
  '/budgets/:id/virtualcards': AnalyticsPageName.BudgetVirtualCards,
  '/budgets/:id/transactions': AnalyticsPageName.BudgetTransactions,
  '/budgets/:id/history': AnalyticsPageName.BudgetHistory,
  '/budgets/:id/update': AnalyticsPageName.UpdateBudgetFlyout,
  '/budgets/:id/deactivate': AnalyticsPageName.DeactivateBudgetModal,
  '/budgets/:id/deactivate/': AnalyticsPageName.DeactivateBudgetModal,
  '/budgetrequests/:id/update': AnalyticsPageName.UpdateBudgetRequestFlyout,

  '/expenses/transactions': AnalyticsPageName.Transactions,
  '/expenses/cycles': AnalyticsPageName.ExpenseReportCycles,
  '/expenses/cycles/:id': AnalyticsPageName.ExpenseReportCycleDetail,
  '/expenses/cycles/:id/update':
    AnalyticsPageName.UpdateExpenseReportCycleSettings,
  '/expenses/cycles/:id/report/:id': AnalyticsPageName.UpdateExpenseReport,
  '/expenses/outofpocket': AnalyticsPageName.ExpensesOutOfPocketReports,
  '/transactions/:id': AnalyticsPageName.TransactionDetail,
  '/downloadtransactions': AnalyticsPageName.DownloadTransactions,

  '/orgs/create': AnalyticsPageName.CreateOrganization,
  '/orgs/invitation/:id': AnalyticsPageName.OrganizationInvitation,
  '/orginvitedecision': AnalyticsPageName.OrganizationInviteDecision,
  '/orgs/:id/remove': AnalyticsPageName.RemoveUserFromOrganization,
  '/organizationSettings/:id/expensecategories':
    AnalyticsPageName.OrgSettingsExpenseCategoriesTab,
  '/organizationSettings/:id/expensecategories/:id':
    AnalyticsPageName.UpdateExpenseCategory,
  '/organizationSettings/:id/expensecategories/:id/bulkupload':
    AnalyticsPageName.BulkUploadExpenseLabels,
  '/organizationSettings/:id/rules':
    AnalyticsPageName.OrgSettingsAccountingAutomationTab,
  '/organizationSettings/:id/rules/create':
    AnalyticsPageName.CreateAccountingAutomationRule,
  '/organizationSettings/:id/departments':
    AnalyticsPageName.OrgSettingsOrganizationDepartmentsTab,
  '/organizationSettings/:id/departments/:id':
    AnalyticsPageName.DepartmentDetailPage,
  '/organizationSettings/:id/integrations':
    AnalyticsPageName.OrgSettingsOrganizationIntegrationsTab,
  '/organizationSettings/:id/integrations/:id/manage':
    AnalyticsPageName.ManageOrganizationIntegration,
  '/organizationSettings/:id/expenseSettings':
    AnalyticsPageName.OrgSettingsExpenseCategoriesTab,
  '/organizationSettings/:id/businessrules':
    AnalyticsPageName.OrgSettingsBusinessRulesTab,
  '/organizationSettings/:id/organizationinfo':
    AnalyticsPageName.OrgSettingsOrganizationInfoTab,
  '/organizationSettings/:id/apikeys': AnalyticsPageName.OrgSettingsApiKeysTab,

  '/people/contacts': AnalyticsPageName.ContactList,
  '/people/contacts/:id': AnalyticsPageName.UserDetail,
  '/people/members': AnalyticsPageName.MemberList,
  '/users/:id': AnalyticsPageName.UserDetail,
  '/people/members/:id': AnalyticsPageName.UserDetail,
  '/people/invited': AnalyticsPageName.InvitedToOrgList,
  '/people/invited/:id': AnalyticsPageName.UserDetail,
  '/invite/people': AnalyticsPageName.InvitePeople,
  '/people/invited/cancel/:id': AnalyticsPageName.CancelInvite,

  '/profile/info': AnalyticsPageName.ProfileInfo,
  '/profile/photo': AnalyticsPageName.UpdateProfilePhoto,
  '/profile/manage': AnalyticsPageName.ManageProfile,
  '/profile/notifications': AnalyticsPageName.Notifications,
  '/profile/security': AnalyticsPageName.Security,
  '/profile/security/changepassword': AnalyticsPageName.ChangePassword,
  '/profile/security/devices': AnalyticsPageName.ManageTrustedDevices,
  '/profile/overview': AnalyticsPageName.ProfileOverview,
  '/profile/overview/apikeys': AnalyticsPageName.ApiKeys,

  '/virtualcards/create': AnalyticsPageName.CreateVirtualCard,
  '/virtualcards/request': AnalyticsPageName.RequestVirtualCard,
  '/virtualcards/allcards': AnalyticsPageName.AllCardsTab,
  '/virtualcards/yourcards': AnalyticsPageName.YourCardsTab,
  '/virtualcards/needsapproval': AnalyticsPageName.VirtualCardsNeedsApprovalTab,
  '/virtualcards/:id': AnalyticsPageName.VirtualCardDetail,
  '/virtualcards/:id/update': AnalyticsPageName.UpdateVirtualCard,
  '/virtualcards/:id/close': AnalyticsPageName.CloseVirtualCard,
  '/virtualcards/:id/deactivate/': AnalyticsPageName.DeactivateVirtualCard,
  '/virtualcards/:id/screen/': AnalyticsPageName.ScreenVirtualCard,

  '/signin': AnalyticsPageName.SignIn,
  '/signup': AnalyticsPageName.SignUp,
  '/onboarding': AnalyticsPageName.Onboarding,
  '/resetpassword': AnalyticsPageName.ForgotPassword,
  '/verifyemail': AnalyticsPageName.VerifyEmailSignUp,
  '/home': AnalyticsPageName.Home,
  '/notifications': AnalyticsPageName.Notifications,
  '/search': AnalyticsPageName.Search,
  '/whatsnew': AnalyticsPageName.WhatsNew,
  '/help': AnalyticsPageName.Help,
  '/faq': AnalyticsPageName.FAQ,
  '/terms': AnalyticsPageName.Terms,
  '/privacy': AnalyticsPageName.Privacy,
  '/support': AnalyticsPageName.Support,
  '/support-request': AnalyticsPageName.SupportRequest,
}
